/**
 * Utility functions for JWT token decoding
 */

export interface DecodedToken {
  sub?: string;
  userType?: string;
  schoolId?: string;
  rights?: string[];
  isSuperAdmin?: boolean;
  authorizedSchoolIds?: string[];
  exp?: number;
  iat?: number;
}

/**
 * Decode JWT token without verification (client-side only)
 * Note: This does NOT verify the signature - backend always verifies
 */
export const decodeToken = (token: string): DecodedToken | null => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded) as DecodedToken;
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
};

/**
 * Extract rights from JWT token
 */
export const extractRights = (token: string | null): string[] => {
  if (!token) return [];
  
  const decoded = decodeToken(token);
  return decoded?.rights || [];
};

/**
 * Check if user has a specific right
 */
export const hasRight = (rights: string[], requiredRight: string): boolean => {
  return rights.includes(requiredRight);
};

/**
 * Check if user has any of the required rights
 */
export const hasAnyRight = (rights: string[], requiredRights: string[]): boolean => {
  return requiredRights.some(right => rights.includes(right));
};

/**
 * Check if user has all required rights
 */
export const hasAllRights = (rights: string[], requiredRights: string[]): boolean => {
  return requiredRights.every(right => rights.includes(right));
};

/**
 * Check if token is expired
 */
export const isTokenExpired = (token: string | null): boolean => {
  if (!token) return true;
  
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) return true;
  
  // exp is in seconds, Date.now() is in milliseconds
  const expirationTime = decoded.exp * 1000;
  return Date.now() >= expirationTime;
};

